import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Segment } from '@shared/types.ts';
import { daysBetween, formatClock, formatDay } from '@shared/time.ts';
import { cityRoute, flightLabel, journeyShareText, shareText, shortRange, viaText } from '@shared/itinerary.ts';
import { connectionsOf, journeysOf, layoverText, needsCare, type Connection } from '@shared/journeys.ts';
import { getStore, useAppState, useSegments } from '../../data/store.ts';
import { AfterFirstPaint, ScreenTitle } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { cameBack, Link } from '../../lib/router.tsx';
import { copyText, useNow } from '../../lib/hooks.ts';
import { SyncButton } from './SyncPanel.tsx';
import { WhoChips } from './Who.tsx';
import { entryText, segmentIcon } from './segments.ts';
import { FILTERS, groupByDay, nearNow, pastYears, travelList, untilLabel, type MonthGroup, type TravelFilter } from './travelList.ts';

const FILTER_KEY = 'natangelo:travel-filter';

/** Years open under "Show past" when you last left the list (put back when you come Back). */
let lastYearsOpen = 0;

/** The pill picked last on this phone (a per-device convenience; storage may be unavailable). */
function savedFilter(): TravelFilter {
  try {
    const v = localStorage.getItem(FILTER_KEY);
    return FILTERS.some((f) => f.id === v) ? (v as TravelFilter) : 'all';
  } catch {
    return 'all';
  }
}

function saveFilter(f: TravelFilter): void {
  try {
    localStorage.setItem(FILTER_KEY, f);
  } catch {
    // Not remembered; nothing else depends on it.
  }
}

const NOUN: Record<TravelFilter, [string, string]> = {
  all: ['booking', 'bookings'],
  flights: ['flight', 'flights'],
  stays: ['stay', 'stays'],
  other: ['booking', 'bookings'],
};

export function TravelScreen() {
  const emails = useAppState((s) => s.emails);
  const attention = emails.filter((e) => e.status === 'failed').length;
  // The title shows the moment the tab is tapped; the list (sorting and grouping every
  // booking) renders right after that first paint, with a shimmer if it takes a moment.
  return (
    <div className="screen">
      <ScreenTitle title="Travel" right={<SyncButton />} />

      {attention > 0 && (
        <Link to="/trips/attention" className="banner banner-orange">
          <Icon name="warning" size={20} />
          <span className="grow" style={{ fontSize: 15, fontWeight: 600 }}>
            {attention} email{attention === 1 ? '' : 's'} need{attention === 1 ? 's' : ''} a look
          </span>
          <Icon name="chevron-right" size={18} />
        </Link>
      )}

      <AfterFirstPaint fallback={<TravelSkeleton />}>
        <TravelBody />
      </AfterFirstPaint>
    </div>
  );
}

function TravelSkeleton() {
  return (
    <div className="stack" aria-busy="true" aria-label="Loading bookings">
      <div className="skeleton" style={{ height: 196, borderRadius: 18 }} />
      <div className="row" style={{ gap: 8 }}>
        {[56, 76, 66, 70].map((w) => (
          <div key={w} className="skeleton" style={{ width: w, height: 44, borderRadius: 22 }} />
        ))}
      </div>
      <div className="skeleton" style={{ width: 120, height: 14, marginTop: 14 }} />
      {[0, 1, 2].map((i) => (
        <div key={i} className="skeleton" style={{ height: 88, borderRadius: 14 }} />
      ))}
    </div>
  );
}

function TravelBody() {
  const store = getStore();
  const segments = useSegments();
  const inbox = useAppState((s) => s.settings.inboxAddress);
  const homeCodes = useAppState((s) => s.settings.homeCodes);
  const now = useNow(60_000);
  const [filter, setFilter] = useState<TravelFilter>(savedFilter);
  // How many years of history are open under "Show past" (0: folded), newest first. Coming
  // Back from a booking reopens them, so the list is as long as it was and the scroll fits.
  const [yearsOpen, setYearsOpen] = useState(() => (cameBack() ? lastYearsOpen : 0));
  useEffect(() => {
    lastYearsOpen = yearsOpen;
  }, [yearsOpen]);
  const showPast = yearsOpen > 0;

  const list = useMemo(() => travelList(segments, filter, now), [segments, filter, now]);
  const upcoming = useMemo(() => groupByDay(list.upcoming, now), [list, now]);
  // Upcoming first: layovers and the next journey only look at bookings from two days ago on.
  // Layovers come from every flight, so they show whatever the pills hide.
  const recent = useMemo(() => nearNow(segments, now), [segments, now]);
  const layovers = useMemo(() => layoverMap(recent, homeCodes), [recent, homeCodes]);
  // The rest of the next flight's journey, for the card's "New York → Ho Chi Minh City".
  const nextJourney = useMemo(() => {
    const next = list.nextFlight;
    if (!next) return [];
    const journey = journeysOf(recent, homeCodes).find((j) => j.some((s) => s.id === next.id)) ?? [next];
    return journey.slice(journey.findIndex((s) => s.id === next.id));
  }, [list, recent, homeCodes]);

  // The past is worked out only once it's opened, a year at a time.
  const years = useMemo(() => (showPast ? pastYears(list.past) : []), [showPast, list]);
  const openYears = years
    .slice(0, yearsOpen)
    .map((y) => y.year)
    .join();
  const past = useMemo(() => {
    if (!openYears) return { months: [], layovers: new Map<string, Connection>() };
    const inOpenYears = (s: Segment) => openYears.includes(s.start.date.slice(0, 4));
    return {
      months: groupByDay(list.past.filter(inOpenYears), now, true),
      layovers: layoverMap(segments.filter(inOpenYears), homeCodes),
    };
  }, [openYears, list, segments, now, homeCodes]);
  const nextYear = years[yearsOpen];
  const [one, many] = NOUN[filter];

  const pick = (f: TravelFilter) => {
    setFilter(f);
    saveFilter(f);
  };

  // Select, then copy: any bookings, in date order, without codes (for sending to friends).
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const selection = useMemo(
    () =>
      selected && {
        ids: selected,
        toggle: (id: string) =>
          setSelected((cur) => {
            const next = new Set(cur);
            if (!next.delete(id)) next.add(id);
            return next;
          }),
      },
    [selected],
  );
  const copySelected = async () => {
    const picked = segments.filter((s) => selected?.has(s.id) && s.status !== 'cancelled');
    if (!picked.length) {
      store.toast(`Tap the ${many} to copy first`);
      return;
    }
    const ok = await copyText(shareText(picked));
    store.toast(ok ? `Copied ${picked.length} ${picked.length === 1 ? one : many}` : 'Copy failed');
    if (ok) setSelected(null);
  };
  const selectButton = (
    <button type="button" className="text-button" onClick={() => setSelected(selected ? null : new Set())}>
      {selected ? 'Cancel' : 'Select'}
    </button>
  );

  return (
    <>
      {list.nextFlight && <NextFlight journey={nextJourney} now={now} />}

      {segments.length > 0 && (
        <div className="chips" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`chip${filter === f.id ? ' chip-gold' : ''}`} aria-pressed={filter === f.id} onClick={() => pick(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {list.upcoming.length > 0 && (
        <div className="row-between">
          <h2 className="section-label">Upcoming</h2>
          {selectButton}
        </div>
      )}
      <Groups months={upcoming} layovers={layovers} selection={selection} />

      {segments.length === 0 ? (
        <div className="empty">Nothing booked yet. Forward a booking email to the inbox below and it shows up here.</div>
      ) : (
        list.upcoming.length === 0 && <div className="empty">No upcoming {many}.</div>
      )}

      {list.past.length > 0 && (
        <button type="button" className="text-button" style={{ alignSelf: 'center' }} aria-expanded={showPast} onClick={() => setYearsOpen(showPast ? 0 : 1)}>
          {showPast ? 'Hide past' : `Show past (${list.past.length})`}
        </button>
      )}
      {showPast && (
        <>
          <div className="row-between">
            <h2 className="section-label">Past</h2>
            {list.upcoming.length === 0 && selectButton}
          </div>
          <Groups months={past.months} layovers={past.layovers} selection={selection} past />
          {nextYear && (
            <button type="button" className="btn" onClick={() => setYearsOpen((n) => n + 1)}>
              Show {nextYear.year} ({nextYear.count} {nextYear.count === 1 ? one : many})
            </button>
          )}
        </>
      )}

      {/* Room to scroll the last rows out from under the bar. */}
      {selected && <div aria-hidden="true" style={{ height: 64 }} />}
      {selected && (
        <div className="select-bar" role="region" aria-label="Selected bookings">
          <span className="grow">{selected.size === 0 ? `Tap the ${many} to copy` : `${selected.size} selected`}</span>
          <button type="button" className="btn btn-primary" onClick={copySelected}>
            <Icon name="copy" size={16} /> Copy
          </button>
        </div>
      )}

      <Link to="/trips/new" className="btn">
        <Icon name="plus" size={18} />
        Add a booking by hand
      </Link>

      <div className="row" style={{ border: '1px dashed var(--line-strong)', borderRadius: 14, padding: '12px 14px', gap: 10 }}>
        <span className="muted">
          <Icon name="mail" />
        </span>
        <span className="grow stack" style={{ gap: 0 }}>
          <span className="small muted">Forward bookings and receipts to</span>
          <span style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}>{inbox}</span>
        </span>
        <button
          type="button"
          className="btn"
          style={{ minHeight: 36 }}
          onClick={async () => store.toast((await copyText(inbox)) ? 'Inbox address copied' : 'Copy failed')}
        >
          Copy
        </button>
      </div>
    </>
  );
}

/** Each flight's layover before it, by flight id. */
function layoverMap(segments: Segment[], homeCodes: string[]): Map<string, Connection> {
  return new Map(connectionsOf(segments, homeCodes).map((c) => [c.to.id, c]));
}

interface Selection {
  ids: Set<string>;
  toggle: (id: string) => void;
}

function Groups({ months, layovers, selection, past }: { months: MonthGroup[]; layovers: Map<string, Connection>; selection?: Selection | null; past?: boolean }) {
  return (
    <>
      {months.map((m) => (
        <section key={m.key} className="stack" aria-label={m.label}>
          <h3 className="month-header">{m.label}</h3>
          {m.days.map((d) => (
            <div key={d.date} className="stack" style={{ gap: 6 }}>
              <span className={`day-label${past ? ' muted' : ''}`}>{d.label}</span>
              <div className="card-flush">
                {d.segments.map((s) => {
                  const layover = layovers.get(s.id);
                  // Past bookings run newest first, so the layover sits after its later flight.
                  return (
                    <Fragment key={s.id}>
                      {layover && !past && <LayoverRow c={layover} />}
                      <BookingRow s={s} selection={selection} />
                      {layover && past && <LayoverRow c={layover} />}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ))}
    </>
  );
}

/** "LAX 3:25 PM → JFK 11:57 PM +1" */
function Route({ s }: { s: Segment }) {
  const later = s.end?.date ? daysBetween(s.start.date, s.end.date) : 0;
  const at = (p: Segment['from']) => p?.code ?? p?.name ?? '?';
  return (
    <>
      <span className="nowrap">
        {at(s.from)} {formatClock(s.start.time)}
      </span>{' '}
      →{' '}
      <span className="nowrap">
        {at(s.to)} {formatClock(s.end?.time)}
        {later > 0 ? <span className="tiny muted"> +{later}</span> : null}
      </span>
    </>
  );
}

function LayoverRow({ c }: { c: Connection }) {
  return <div className={`layover-row${needsCare(c) ? ' is-tight' : ''}`}>{layoverText(c)}</div>;
}

/** Where first, then when, then who flies it. Codes live on the booking page (one tap away). */
function rowText(s: Segment): { title: ReactNode; route?: ReactNode; sub: string } {
  switch (s.type) {
    case 'flight':
      return { title: cityRoute([s]), route: <Route s={s} />, sub: flightLabel(s) };
    case 'train':
      return { title: cityRoute([s]), route: <Route s={s} />, sub: [s.provider, s.number].filter(Boolean).join(' ') || 'Train' };
    case 'stay': {
      const nights = s.end?.date ? daysBetween(s.start.date, s.end.date) : 0;
      return { title: s.place?.name ?? s.title, sub: [shortRange(s.start.date, s.end?.date), nights ? `${nights} night${nights === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ') };
    }
    default: {
      const e = entryText({ key: s.id, segment: s, date: s.start.date, time: s.start.time, kind: 'start', sortKey: 0 });
      return { title: e.title, sub: [e.time, e.sub].filter(Boolean).join(' · ') };
    }
  }
}

function BookingRow({ s, selection }: { s: Segment; selection?: Selection | null }) {
  const cancelled = s.status === 'cancelled';
  const { title, route, sub } = rowText(s);
  const text = (
    <>
      <span className="row-title">{title}</span>
      {route && <span className="row-route">{route}</span>}
      <span className="small muted">{[cancelled ? 'Cancelled' : '', sub].filter(Boolean).join(' · ')}</span>
    </>
  );
  // Selecting: the whole row is one checkbox (cancelled bookings can't be copied).
  if (selection) {
    const checked = selection.ids.has(s.id);
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={cancelled}
        className={`flight-row select-row${cancelled ? ' is-cancelled' : ''}${checked ? ' is-checked' : ''}`}
        onClick={() => selection.toggle(s.id)}
      >
        <span className="select-box" aria-hidden="true">
          {checked && <Icon name="check" size={16} />}
        </span>
        <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
          {text}
        </span>
      </button>
    );
  }
  return (
    <div className={`flight-row${cancelled ? ' is-cancelled' : ''}`}>
      <span className="event-icon">
        <Icon name={segmentIcon(s.type)} />
      </span>
      <Link to={`/trips/${s.tripId}/${s.id}`} className="grow stack" style={{ gap: 2, color: 'var(--text)', minWidth: 0 }}>
        {text}
      </Link>
      {!cancelled && <WhoChips seg={s} />}
    </div>
  );
}

/**
 * The next journey: where it goes, when it leaves and lands, and Copy trip (every flight in
 * it, without codes, for sending). Code, airline name and bags are on the booking page.
 */
function NextFlight({ journey, now }: { journey: Segment[]; now: Date }) {
  const store = getStore();
  const first = journey[0];
  const last = journey.at(-1)!;
  const via = viaText(journey);
  const landing = last.end ?? last.start;
  return (
    <section className="next-card" aria-label="Next flight">
      <span className="next-eyebrow">NEXT FLIGHT · {untilLabel(first.start.date, now).toUpperCase()}</span>
      <Link to={`/trips/${first.tripId}/${first.id}`} className="stack" style={{ gap: 2, color: 'var(--on-gold)' }}>
        <span className="next-title">{cityRoute(journey)}</span>
        {via && (
          <span className="small">
            {via} · {journey.length} flights
          </span>
        )}
        <span className="next-times">
          <span className="stack" style={{ gap: 0 }}>
            <span className="tiny">Departs {formatDay(first.start.date)}</span>
            <span className="next-time">{formatClock(first.start.time) || '—'}</span>
            <span className="small">{[first.from?.code, first.number].filter(Boolean).join(' · ')}</span>
          </span>
          <span className="stack" style={{ gap: 0 }}>
            <span className="tiny">Arrives {formatDay(landing.date)}</span>
            <span className="next-time">{formatClock(last.end?.time) || '—'}</span>
            <span className="small">{last.to?.code}</span>
          </span>
        </span>
      </Link>
      <div className="row-between">
        <WhoChips seg={first} onGold />
        <button
          type="button"
          className="next-copy"
          onClick={async () => store.toast((await copyText(journeyShareText(journey))) ? 'Trip copied' : 'Copy failed')}
        >
          <Icon name="copy" size={16} /> Copy trip
        </button>
      </div>
    </section>
  );
}
